/**
 * Grabs the page the reader is looking at, so a report carries a picture and
 * the help desk does not have to ask "what were you seeing?".
 *
 * Mirrors Cockpit's capture (`ui/src/lib/supportIssue.ts`): the app's main
 * region, not the whole window, so the header and the bottom nav stay out of
 * the shot. Any failure returns null and the report still files without one;
 * a picture is never worth blocking a problem report.
 */

import { toPng } from 'html-to-image';

export interface SupportScreenshot {
  filename: string;
  /** Matches the shared dialog's union; this capture always produces PNG. */
  content_type: 'image/png' | 'image/jpeg' | 'image/webp';
  base64: string;
}

function screenshotFilename(tool?: string): string {
  const label = (tool ?? 'sweet-spot').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${label || 'sweet-spot'}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
}

export async function captureSweetSpotScreenshot(tool?: string): Promise<SupportScreenshot | null> {
  const node = document.querySelector<HTMLElement>('[data-sweetspot-main]') ?? document.body;
  try {
    const dataUrl = await toPng(node, {
      cacheBust: true,
      // Cap the ratio: a store tablet at 3x would produce a needlessly large
      // attachment for a screenshot nobody zooms into.
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      backgroundColor: document.documentElement.classList.contains('dark') ? '#221A26' : '#FFF8EF',
    });
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    return base64 ? { filename: screenshotFilename(tool), content_type: 'image/png', base64 } : null;
  } catch {
    return null;
  }
}
